import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import MagasinBadge from '../components/MagasinBadge';
import { printTicket80mm } from '../utils/ticket80mmPrint';

const CaisseHistoriquePage = () => {
  const { api, isAdmin } = useAuth();
  const { t, tError, formatCurrency, formatDate } = useLanguage();
  const [caisses, setCaisses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_debut: '', date_fin: '', magasin_id: '' });
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [monthlyMonth, setMonthlyMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [forceClosingId, setForceClosingId] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const handlePrint = async (caisseId) => {
    try {
      const response = await api.get(`/caisse/print/${caisseId}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'width=900,height=700');
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('caisseHist.errPrint'));
    }
  };

  const handleMonthlyPrint = async (type, caisseId) => {
    const variant = type === 'ouverture' ? 'ouverture' : type === 'fermeture' ? 'fermeture' : type === 'ventes' ? 'ventes' : null;
    if (!variant) {
      await handlePrint(caisseId);
      return;
    }
    try {
      await printTicket80mm(api, { type: 'caisse', ref: caisseId, variant });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('caisseHist.errPrintGeneric'));
    }
  };

  const fetchMonthly = async (month = monthlyMonth) => {
    setMonthlyLoading(true);
    try {
      const params = { month };
      if (filters.magasin_id) params.magasin_id = filters.magasin_id;
      const response = await api.get('/caisse/monthly', { params });
      setMonthlyRows(response.data.rows || []);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('caisseHist.errLoadMonthly'));
    } finally {
      setMonthlyLoading(false);
    }
  };

  const handleMonthlyExport = async () => {
    try {
      const response = await api.get('/caisse/export', {
        params: { month: monthlyMonth, ...(filters.magasin_id ? { magasin_id: filters.magasin_id } : {}) },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `caisse_comptable_${monthlyMonth}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('caisseHist.errExport'));
    }
  };

  const fetchHistorique = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      if (filters.magasin_id) params.append('magasin_id', filters.magasin_id);
      const response = await api.get(`/caisse/historique?${params.toString()}`);
      setCaisses(response.data);
    } catch (error) {
      console.error('Error fetching caisse historique', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistorique();
    fetchMonthly();
  }, []);

  const handleForceClose = async (caisseId, dateCaisse) => {
    if (!window.confirm(t('caisseHist.confirmForceClose', { date: dateCaisse }))) return;
    setForceClosingId(caisseId);
    try {
      await api.post(`/caisse/force-close/${caisseId}`);
      fetchHistorique();
      fetchMonthly();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('caisseHist.errForceClose'));
    } finally { setForceClosingId(null); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto fade-in" data-testid="caisse-historique-page">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-500 mb-6">
          🧾 {t('caisseHist.title')}
        </h2>

        <div className="glass p-4 mb-6" data-testid="caisse-historique-mensuel">
          <div className="flex flex-col md:flex-row md:items-end gap-4 mb-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('caisseHist.month')}</label>
              <input
                type="month"
                value={monthlyMonth}
                onChange={(e) => setMonthlyMonth(e.target.value)}
                className="input-dark py-2 text-sm"
                data-testid="caisse-month-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('caisseHist.store')}</label>
              <select
                value={filters.magasin_id}
                onChange={(e) => setFilters({ ...filters, magasin_id: e.target.value })}
                className="input-dark py-2 text-sm"
                data-testid="caisse-month-magasin"
              >
                <option value="">{t('caisseHist.allStores')}</option>
                <option value="M1">M1</option>
                <option value="M2">M2</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchMonthly(monthlyMonth)}
                className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold"
                data-testid="caisse-month-filter"
              >
                {t('common.filter')}
              </button>
              <button
                onClick={handleMonthlyExport}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold"
                data-testid="caisse-month-export"
              >
                {t('caisseHist.exportAccounting')}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="table-dark w-full text-sm" data-testid="caisse-monthly-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-gray-400">
                  <th>{t('common.date')}</th>
                  <th>{t('caisseHist.store')}</th>
                  <th>{t('caisseHist.totalSales')}</th>
                  <th>{t('caisseHist.totalCash')}</th>
                  <th>{t('caisseHist.totalCard')}</th>
                  <th>Stripe</th>
                  <th>{t('caisseHist.withdrawals')}</th>
                  <th>{t('caisseHist.totalTheoretical')}</th>
                  <th>{t('caisseHist.totalAccounting')}</th>
                  <th>{t('caisseHist.discrepancy')}</th>
                  <th>{t('caisseHist.validatedBy')}</th>
                  <th>{t('caisseHist.validationDate')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {monthlyLoading && (
                  <tr>
                    <td colSpan="13" className="text-center py-6 text-gray-400">{t('common.loading')}</td>
                  </tr>
                )}
                {!monthlyLoading && monthlyRows.length === 0 && (
                  <tr>
                    <td colSpan="13" className="text-center py-6 text-gray-400">{t('caisseHist.noValidatedCaisse')}</td>
                  </tr>
                )}
                {!monthlyLoading && monthlyRows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.date_caisse}</td>
                    <td><MagasinBadge code={row.magasin_id || 'M1'} size="sm" /></td>
                    <td>{formatCurrency(row.total_ventes || 0)}</td>
                    <td>{formatCurrency(row.total_especes || 0)}</td>
                    <td>{formatCurrency(row.total_cb || 0)}</td>
                    <td className="text-blue-300">{formatCurrency(row.total_stripe || 0)}</td>
                    <td>{formatCurrency(row.total_retraits || 0)}</td>
                    <td>{formatCurrency(row.total_theorique || 0)}</td>
                    <td>{formatCurrency(row.total_comptable || 0)}</td>
                    <td className={row.ecart >= 0 ? 'text-emerald-300' : 'text-red-400'}>{formatCurrency(row.ecart || 0)}</td>
                    <td>{row.validated_by || '-'}</td>
                    <td>{row.validated_at ? formatDate(row.validated_at) : '-'}</td>
                    <td>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleMonthlyPrint('ouverture', row.id)}
                          className="px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs"
                          data-testid={`caisse-month-open-${row.id}`}
                        >
                          {t('caisseHist.opening')}
                        </button>
                        <button
                          onClick={() => handleMonthlyPrint('fermeture', row.id)}
                          className="px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs"
                          data-testid={`caisse-month-close-${row.id}`}
                        >
                          {t('caisseHist.closing')}
                        </button>
                        <button
                          onClick={() => handleMonthlyPrint('rapport', row.id)}
                          className="px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs"
                          data-testid={`caisse-month-report-${row.id}`}
                        >
                          {t('caisseHist.caisse')}
                        </button>
                        <button
                          onClick={() => handleMonthlyPrint('ventes', row.id)}
                          className="px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs"
                          data-testid={`caisse-month-ventes-${row.id}`}
                        >
                          {t('caisseHist.daySales')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="glass p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('caisseHist.dateStart')}</label>
              <input
                type="date"
                value={filters.date_debut}
                onChange={(e) => setFilters({ ...filters, date_debut: e.target.value })}
                className="input-dark w-full py-2 text-sm"
                data-testid="caisse-filter-date-debut"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('caisseHist.dateEnd')}</label>
              <input
                type="date"
                value={filters.date_fin}
                onChange={(e) => setFilters({ ...filters, date_fin: e.target.value })}
                className="input-dark w-full py-2 text-sm"
                data-testid="caisse-filter-date-fin"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('caisseHist.store')}</label>
              <select
                value={filters.magasin_id}
                onChange={(e) => setFilters({ ...filters, magasin_id: e.target.value })}
                className="input-dark w-full py-2 text-sm"
                data-testid="caisse-filter-magasin"
              >
                <option value="">{t('caisseHist.allStores')}</option>
                <option value="M1">M1</option>
                <option value="M2">M2</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchHistorique}
                className="flex-1 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold"
                data-testid="caisse-filter-apply"
              >
                {t('common.filter')}
              </button>
              <button
                onClick={() => {
                  setFilters({ date_debut: '', date_fin: '', magasin_id: '' });
                  fetchHistorique();
                  fetchMonthly();
                }}
                className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs text-gray-300"
                data-testid="caisse-filter-reset"
              >
                {t('caisseHist.reset')}
              </button>
            </div>
          </div>
        </div>

        <div className="glass overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-dark-custom text-sm">
              <thead>
                <tr>
                  <th>{t('common.date')}</th>
                  <th>{t('caisseHist.store')}</th>
                  <th>{t('caisseHist.opening')}</th>
                  <th>{t('caisseHist.closing')}</th>
                  <th>{t('caisseHist.cashFloat')}</th>
                  <th>{t('caisseHist.totalCash')}</th>
                  <th>{t('caisseHist.totalCard')}</th>
                  <th>Stripe</th>
                  <th>{t('caisseHist.totalSales')}</th>
                  <th>{t('caisseHist.discrepancy')}</th>
                  <th>{t('caisseHist.openedBy')}</th>
                  <th>{t('caisseHist.closedBy')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="13" className="text-center py-8">
                      <div className="loading-spinner mx-auto"></div>
                    </td>
                  </tr>
                ) : caisses.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="text-center py-8 text-gray-400">
                      {t('caisseHist.noHistory')}
                    </td>
                  </tr>
                ) : (
                  (showAll ? caisses : caisses.slice(0, 2)).map((caisse) => (
                    <tr key={caisse.id} data-testid={`caisse-row-${caisse.id}`}>
                      <td className="font-semibold">{caisse.date_caisse}</td>
                      <td><MagasinBadge code={caisse.magasin_id || 'M1'} size="sm" /></td>
                      <td>{caisse.heure_ouverture ? formatDate(caisse.heure_ouverture, { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                      <td>{caisse.heure_fermeture ? formatDate(caisse.heure_fermeture, { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                      <td className="text-cyan-400 font-bold">{formatCurrency(caisse.fond_caisse || 0)}</td>
                      <td className="text-green-400 font-bold">{formatCurrency(caisse.total_especes || 0)}</td>
                      <td className="text-blue-400 font-bold">{formatCurrency(caisse.total_cb || 0)}</td>
                      <td className="text-blue-300 font-bold">{formatCurrency(caisse.total_stripe || 0)}</td>
                      <td className="text-emerald-300 font-bold">{formatCurrency(caisse.total_ventes || 0)}</td>
                      <td className={(caisse.ecart || 0) >= 0 ? 'text-emerald-300 font-bold' : 'text-red-400 font-bold'}>
                        {formatCurrency(caisse.ecart || 0)}
                      </td>
                      <td>{caisse.opened_by || caisse.user_ouverture || '-'}</td>
                      <td>{caisse.closed_by || caisse.user_fermeture || '-'}</td>
                      <td>
                        <div className="flex gap-1 flex-wrap">
                          <button
                            onClick={() => handlePrint(caisse.id)}
                            className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold"
                            data-testid={`caisse-print-${caisse.id}`}
                          >
                            {t('common.print')}
                          </button>
                          {!caisse.heure_fermeture && isAdmin && (
                            <button
                              onClick={() => handleForceClose(caisse.id, caisse.date_caisse)}
                              disabled={forceClosingId === caisse.id}
                              className="px-3 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs font-semibold border border-red-500/30 disabled:opacity-50"
                              data-testid={`caisse-force-close-${caisse.id}`}
                            >
                              {forceClosingId === caisse.id ? t('caisseHist.closingProgress') : t('caisseHist.forceClose')}
                            </button>
                          )}
                          {caisse.force_closed && (
                            <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-400 text-xs border border-red-500/30">{t('caisseHist.forced')}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!loading && caisses.length > 2 && !showAll && (
          <div className="flex justify-center mt-4">
            <button
              onClick={() => setShowAll(true)}
              className="px-6 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold transition-all"
              data-testid="caisse-voir-plus-btn"
            >
              {t('caisseHist.showMore', { count: caisses.length - 2 })}
            </button>
          </div>
        )}
        {showAll && caisses.length > 2 && (
          <div className="flex justify-center mt-4">
            <button
              onClick={() => setShowAll(false)}
              className="px-6 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-all"
              data-testid="caisse-voir-moins-btn"
            >
              {t('caisseHist.showLess')}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CaisseHistoriquePage;
